import { Fragment, useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, FileText, Folder, Loader2, NotebookPen, Palette, RotateCcw, SendHorizontal, Sparkles, X } from 'lucide-react';
import { useOptionalMeetingNotes } from '../../lib/meetingNotes';
import { MeetingVisitCard } from './MeetingVisitCard';
import {
  COMPANION_HUES,
  COMPANION_NAME_MAX,
  type CompanionAction,
  type CompanionMessageView,
  type CompanionProposal,
  VAULT_REFERENCE_PATTERN,
} from '@homework/shared';
import { useCompanion } from '../../lib/companion';
import { useCalls } from '../../lib/calls';
import { useMe } from '../../lib/auth';
import { companionPalette } from '../../lib/avatarArt';
import { CompanionCharacter } from './CompanionCharacter';
import { cx, ErrorState, Spinner } from '../ui';

/**
 * The floating companion: a 3D character in the corner of every page, and
 * the panel it opens to talk through the day. Suggestions arrive as cards and
 * nothing happens until the person presses Confirm.
 */

const NAME_IDEAS = ['Pixel', 'Mochi', 'Nori', 'Bolt', 'Sprout', 'Biscuit'];

const FILE_STARTERS = ['What is this about?', 'Summarise the key points', 'What do I need to act on?'];

const STARTERS = [
  'What’s on for me today?',
  'Help me plan my morning',
  'What should I focus on first?',
  'Anything I need to catch up on?',
];

export function CompanionWidget() {
  const { companion, mood, open, setOpen } = useCompanion();
  const visit = useOptionalMeetingNotes()?.myVisit ?? null;
  const { activeCall } = useCalls();
  const launcherRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const name = companion?.name ?? null;
  const hue = companion?.hue ?? 250;

  const close = () => {
    setOpen(false);
    launcherRef.current?.focus();
  };

  return (
    <div
      className={cx(
        'fixed bottom-4 z-40 flex flex-col items-end gap-3',
        // A call panel owns the bottom-right corner while it is up.
        activeCall ? 'right-4 hidden sm:right-[calc(min(22rem,100vw-2rem)+2rem)] sm:flex' : 'right-4',
      )}
    >
      {open ? <CompanionPanel id={panelId} onClose={close} /> : null}

      <div className="flex items-end gap-2">
        {!open && companion && !name ? (
          <div className="glass-strong mb-6 hidden animate-slide-up rounded-3xl rounded-br-md px-3 py-2 text-sm font-medium text-slate-700 sm:block">
            Hi! Want to give me a name?
          </div>
        ) : !open && visit ? (
          <div className="glass-strong mb-6 hidden animate-slide-up rounded-3xl rounded-br-md px-3 py-2 text-sm font-medium text-slate-700 sm:block">
            {visit.status === 'SUMMARIZING' ? `Writing up ${visit.roomName}…` : `Taking notes in ${visit.roomName}`}
          </div>
        ) : null}
        <button
          ref={launcherRef}
          type="button"
          onClick={() => (open ? close() : setOpen(true))}
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          aria-label={
            open ? `Close ${name ?? 'your companion'}` : name ? `Talk to ${name}, your companion` : 'Meet your companion'
          }
          className="group relative h-24 w-24 rounded-full outline-none transition-transform duration-200 hover:-translate-y-0.5 focus-visible:ring-4 focus-visible:ring-brand-400/60"
        >
          <span
            className="glass absolute inset-x-3 bottom-1 top-6 rounded-full transition-opacity group-hover:opacity-100"
            style={{ boxShadow: `0 10px 30px -8px ${companionPalette(hue).base}88` }}
            aria-hidden
          />
          <CompanionCharacter hue={hue} mood={mood} className="absolute inset-0" />
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- panel

function CompanionPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { companion, loading, loadError, reload } = useCompanion();
  const [customising, setCustomising] = useState(false);
  const [meetingForm, setMeetingForm] = useState(false);
  const titleId = useId();

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const named = Boolean(companion?.name);

  return (
    <section
      id={id}
      role="dialog"
      aria-labelledby={titleId}
      className="glass-strong flex h-[min(38rem,calc(100vh-10rem))] w-[min(26rem,calc(100vw-2rem))] animate-slide-up flex-col overflow-hidden rounded-4xl"
    >
      <header className="flex items-center gap-2 border-b border-white/60 px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 id={titleId} className="truncate font-bold text-slate-900">
            {companion?.name ?? 'Your companion'}
          </h2>
          <CompanionStatusLine />
        </div>
        {named ? (
          <>
            <button
              type="button"
              className={cx('btn-ghost p-2', customising && 'bg-white/70')}
              onClick={() => setCustomising((value) => !value)}
              aria-pressed={customising}
              aria-label="Rename or recolour"
            >
              <Palette className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              className={cx('btn-ghost p-2', meetingForm && 'bg-white/70')}
              onClick={() => setMeetingForm((value) => !value)}
              aria-pressed={meetingForm}
              aria-label="Send to a meeting to take notes"
              title="Send to a meeting"
            >
              <NotebookPen className="h-4 w-4" aria-hidden />
            </button>
            <StartOverButton />
          </>
        ) : null}
        <button type="button" className="btn-ghost p-2" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" aria-hidden />
        </button>
      </header>

      {loading && !companion ? (
        <div className="flex flex-1 items-center justify-center" role="status">
          <Spinner label="Waking up…" />
        </div>
      ) : loadError && !companion ? (
        <div className="p-4">
          <ErrorState error={loadError} onRetry={reload} title="Your companion couldn’t wake up" />
        </div>
      ) : !named ? (
        <NamingStep />
      ) : (
        <>
          {customising ? <Customise onDone={() => setCustomising(false)} /> : null}
          <MeetingVisitCard name={companion?.name ?? 'Mochi'} openForm={meetingForm} onFormClosed={() => setMeetingForm(false)} />
          <Conversation onMeeting={() => setMeetingForm(true)} />
        </>
      )}
    </section>
  );
}

function CompanionStatusLine() {
  const { activity, sending, draft, companion } = useCompanion();
  const text = activity
    ? `${activity}…`
    : sending && !draft
      ? 'Thinking…'
      : companion && !companion.aiAvailable
        ? 'Not connected yet'
        : 'Here to help plan your day';
  return <p className="truncate text-xs text-slate-500">{text}</p>;
}

function StartOverButton() {
  const { startOver, messages, sending } = useCompanion();
  const [pending, setPending] = useState(false);
  return (
    <button
      type="button"
      className="btn-ghost p-2"
      disabled={pending || sending || messages.length === 0}
      aria-label="Start today’s conversation over"
      title="Start over"
      onClick={async () => {
        if (!window.confirm('Clear today’s conversation and start fresh?')) return;
        setPending(true);
        try {
          await startOver();
        } finally {
          setPending(false);
        }
      }}
    >
      <RotateCcw className="h-4 w-4" aria-hidden />
    </button>
  );
}

// ---------------------------------------------------------------- naming

function HueSwatches({ value, onPick }: { value: number; onPick: (hue: number) => void }) {
  return (
    <div role="radiogroup" aria-label="Colour" className="flex flex-wrap gap-2">
      {COMPANION_HUES.map((hue) => {
        const palette = companionPalette(hue);
        const selected = hue === value;
        return (
          <button
            key={hue}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`Colour ${COMPANION_HUES.indexOf(hue) + 1}`}
            onClick={() => onPick(hue)}
            className={cx(
              'h-7 w-7 rounded-full ring-2 ring-offset-2 ring-offset-white/60 transition',
              selected ? 'ring-slate-800' : 'ring-transparent hover:ring-white',
            )}
            style={{ background: `radial-gradient(circle at 35% 30%, ${palette.light}, ${palette.base} 55%, ${palette.dark})` }}
          />
        );
      })}
    </div>
  );
}

function NamingStep() {
  const { companion, rename, repaint } = useCompanion();
  const me = useMe();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const firstName = me.user.name.split(' ')[0] ?? me.user.name;

  useEffect(() => inputRef.current?.focus(), []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError('Pick a name first — anything you like.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await rename(name);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That didn’t save. Try again?');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="rounded-3xl bg-white/70 p-4 text-sm text-slate-700 ring-1 ring-inset ring-white">
        <p className="font-semibold text-slate-900">Hi {firstName}! 👋</p>
        <p className="mt-1">
          I’m your new office buddy. I can look at your calendar, tasks and leave, and help you work out what to do
          today. Only you can see what we talk about.
        </p>
      </div>

      <div>
        <label htmlFor={inputId} className="label">
          What would you like to call me?
        </label>
        <input
          ref={inputRef}
          id={inputId}
          className="input mt-1"
          value={name}
          maxLength={COMPANION_NAME_MAX}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Pixel"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {NAME_IDEAS.map((idea) => (
            <button
              key={idea}
              type="button"
              className="badge bg-white/70 text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-white"
              onClick={() => setName(idea)}
            >
              {idea}
            </button>
          ))}
        </div>
        {error ? (
          <p id={`${inputId}-error`} className="mt-2 text-sm text-rose-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <div>
        <p className="label mb-2">And my colour</p>
        <HueSwatches value={companion?.hue ?? 250} onPick={(hue) => void repaint(hue)} />
      </div>

      <button type="submit" className="btn-primary mt-auto self-stretch" disabled={saving}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Sparkles className="h-4 w-4" aria-hidden />}
        {name.trim() ? `Nice to meet you, ${name.trim()}!` : 'That’s me!'}
      </button>
    </form>
  );
}

function Customise({ onDone }: { onDone: () => void }) {
  const { companion, rename, repaint } = useCompanion();
  const [name, setName] = useState(companion?.name ?? '');
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();

  const save = async (event: FormEvent) => {
    event.preventDefault();
    try {
      if (name.trim() && name.trim() !== companion?.name) await rename(name);
      onDone();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That didn’t save.');
    }
  };

  return (
    <form onSubmit={save} className="space-y-3 border-b border-white/60 bg-white/40 px-4 py-3">
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <label htmlFor={inputId} className="label">
            Name
          </label>
          <input
            id={inputId}
            className="input mt-1"
            value={name}
            maxLength={COMPANION_NAME_MAX}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <button type="submit" className="btn-secondary">
          Save
        </button>
      </div>
      <HueSwatches value={companion?.hue ?? 250} onPick={(hue) => void repaint(hue)} />
      {error ? (
        <p className="text-sm text-rose-600" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

// ---------------------------------------------------------- conversation

function Conversation({ onMeeting }: { onMeeting: () => void }) {
  const { companion, messages, draft, activity, sending, replyError, send, attachments, removeAttachment } = useCompanion();
  const [text, setText] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const canTalk = companion?.aiAvailable !== false;
  const lastCount = useRef(messages.length);

  useEffect(() => inputRef.current?.focus(), []);

  // Follow the conversation as it grows, including while a reply streams in.
  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, draft, activity]);

  // Screen readers hear each finished reply once, not every streamed word.
  useEffect(() => {
    if (messages.length > lastCount.current) {
      const last = messages.at(-1);
      if (last?.role === 'assistant') setAnnouncement(`${companion?.name ?? 'Companion'} says: ${last.text}`);
    }
    lastCount.current = messages.length;
  }, [messages, companion?.name]);

  const submit = async (value: string) => {
    if (!value.trim() || sending) return;
    setText('');
    const replied = await send(value);
    if (!replied) setText((current) => current || value);
    inputRef.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void submit(text);
    }
  };

  return (
    <>
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {!canTalk ? (
          <div className="rounded-3xl bg-amber-400/15 p-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-500/25">
            I can’t talk just yet — an admin needs to connect me to the AI service by setting{' '}
            <code className="font-mono text-xs">ANTHROPIC_API_KEY</code> on the server. Everything else in HomeWork
            works as normal.
          </div>
        ) : null}

        {messages.length === 0 && !sending ? (
          <div className="space-y-3">
            <Bubble role="assistant">
              Hello! I’m {companion?.name}. Want to go through today together? I can check your meetings, tasks and
              anything waiting for you.
            </Bubble>
            {canTalk ? (
              <div className="flex flex-wrap gap-1.5" aria-label="Suggestions">
                {(attachments.length > 0 ? FILE_STARTERS : STARTERS).map((starter) => (
                  <button
                    key={starter}
                    type="button"
                    className="badge bg-white/70 py-1.5 text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-white"
                    onClick={() => void submit(starter)}
                  >
                    {starter}
                  </button>
                ))}
                {attachments.length === 0 ? (
                  <button
                    type="button"
                    className="badge bg-white/70 py-1.5 text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-white"
                    onClick={onMeeting}
                  >
                    Take notes in a meeting for me
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {messages.map((message) => (
          <MessageItem key={message.id} message={message} />
        ))}

        {sending ? (
          draft ? (
            <Bubble role="assistant">
              <FormattedText text={draft} />
              <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse rounded-sm bg-slate-400 align-middle" aria-hidden />
            </Bubble>
          ) : (
            <Bubble role="assistant">
              <span className="inline-flex items-center gap-2 text-slate-500">
                <TypingDots />
                {activity ? `${activity}…` : 'Thinking…'}
              </span>
            </Bubble>
          )
        ) : null}

        {replyError ? (
          <p className="rounded-2xl bg-rose-400/15 px-3 py-2 text-sm text-rose-800 ring-1 ring-inset ring-rose-500/25" role="alert">
            {replyError}
          </p>
        ) : null}
      </div>

      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>

      {attachments.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 border-t border-white/60 px-3 pt-2" aria-label="Talking about">
          {attachments.map((item) => (
            <span
              key={item.id}
              className="badge max-w-full bg-brand-500/10 text-slate-700 ring-1 ring-inset ring-brand-500/20"
            >
              {item.type === 'file' ? <FileText className="h-3 w-3 shrink-0" aria-hidden /> : <Folder className="h-3 w-3 shrink-0" aria-hidden />}
              <span className="truncate">{item.name}</span>
              <button
                type="button"
                className="-mr-1 rounded-full p-0.5 hover:bg-white/70"
                onClick={() => removeAttachment(item.id)}
                aria-label={`Stop talking about ${item.name}`}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <form
        className="flex items-end gap-2 border-t border-white/60 p-3"
        onSubmit={(event) => {
          event.preventDefault();
          void submit(text);
        }}
      >
        <label htmlFor={inputId} className="sr-only">
          Message {companion?.name}
        </label>
        <textarea
          ref={inputRef}
          id={inputId}
          rows={1}
          className="input max-h-32 min-h-[2.75rem] flex-1 resize-none"
          placeholder={
            !canTalk
              ? 'Not connected yet'
              : attachments.length > 0
                ? `Ask ${companion?.name} about ${attachments.length === 1 ? 'this' : 'these'}…`
                : `Ask ${companion?.name} about your day or your files…`
          }
          value={text}
          disabled={!canTalk}
          maxLength={2000}
          onChange={(event) => {
            setText(event.target.value);
            event.target.style.height = 'auto';
            event.target.style.height = `${event.target.scrollHeight}px`;
          }}
          onKeyDown={onKeyDown}
        />
        <button
          type="submit"
          className="btn-primary h-11 w-11 shrink-0 p-0"
          disabled={!canTalk || sending || !text.trim()}
          aria-label="Send"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <SendHorizontal className="h-4 w-4" aria-hidden />}
        </button>
      </form>
    </>
  );
}

function MessageItem({ message }: { message: CompanionMessageView }) {
  return (
    <div className="space-y-2">
      {message.text ? (
        <Bubble role={message.role}>
          <FormattedText text={message.text} />
        </Bubble>
      ) : null}
      {message.proposals.map((proposal) => (
        <ProposalCard key={proposal.id} proposal={proposal} />
      ))}
    </div>
  );
}

function Bubble({ role, children }: { role: 'user' | 'assistant'; children: ReactNode }) {
  return (
    <div className={cx('flex', role === 'user' ? 'justify-end' : 'justify-start')}>
      <div
        className={cx(
          'max-w-[85%] whitespace-pre-wrap break-words rounded-3xl px-3.5 py-2 text-sm leading-relaxed',
          role === 'user'
            ? 'rounded-br-md bg-brand-500/10 text-slate-800 ring-1 ring-inset ring-brand-500/20'
            : 'rounded-bl-md bg-white/75 text-slate-700 ring-1 ring-inset ring-white',
        )}
        // Arabic replies read right to left, English left to right (AR-2).
        dir="auto"
      >
        {children}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex gap-1" aria-hidden>
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400"
          style={{ animationDelay: `${index * 120}ms` }}
        />
      ))}
    </span>
  );
}

/** Just enough formatting for a chat reply: bullets and **bold**, nothing that can inject markup. */
function FormattedText({ text }: { text: string }) {
  const blocks: Array<{ kind: 'p' | 'ul'; lines: string[] }> = [];
  for (const raw of text.split('\n')) {
    // Markdown headings (Mochi's drafted documents) read as bold lines.
    const heading = /^#{1,6}\s+(.*)$/.exec(raw);
    const line = heading ? `**${heading[1]}**` : raw;
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const last = blocks.at(-1);
    if (bullet) {
      if (last?.kind === 'ul') last.lines.push(bullet[1] ?? '');
      else blocks.push({ kind: 'ul', lines: [bullet[1] ?? ''] });
    } else if (last?.kind === 'p') {
      last.lines.push(line);
    } else {
      blocks.push({ kind: 'p', lines: [line] });
    }
  }
  return (
    <>
      {blocks.map((block, index) =>
        block.kind === 'ul' ? (
          <ul key={index} className="my-1 list-disc space-y-0.5 whitespace-normal pl-5">
            {block.lines.map((line, lineIndex) => (
              <li key={lineIndex}>
                <Inline text={line} />
              </li>
            ))}
          </ul>
        ) : (
          <Fragment key={index}>
            <Inline text={block.lines.join('\n').replace(/^\n+|\n+$/g, '')} />
          </Fragment>
        ),
      )}
    </>
  );
}

/** A Vault file or folder Mochi cited, as a link that opens it (Weco BRD §21, provenance). */
/** A cited file or folder; with a locator ("Page 3") when the fact came from one part of it (VA-8). */
function VaultReference({ type, id, name, locator }: { type: string; id: string; name: string; locator?: string }) {
  const Icon = type === 'file' ? FileText : Folder;
  return (
    <Link
      to={type === 'file' ? `/files?file=${encodeURIComponent(id)}` : `/files/folder/${encodeURIComponent(id)}`}
      className="mx-0.5 inline-flex max-w-full items-center gap-1 rounded-full bg-brand-500/10 px-2 py-0.5 align-baseline text-xs font-semibold text-brand-800 ring-1 ring-inset ring-brand-500/20 hover:bg-brand-500/15"
      aria-label={locator ? `${name}, ${locator}` : name}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      <bdi className="truncate">{name}</bdi>
      {locator ? <span className="shrink-0 font-medium text-brand-700/80">· {locator}</span> : null}
    </Link>
  );
}

function Inline({ text }: { text: string }) {
  const pieces: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(new RegExp(VAULT_REFERENCE_PATTERN.source, 'g'))) {
    const at = match.index ?? 0;
    if (at > last) pieces.push(<Bold key={`t${last}`} text={text.slice(last, at)} />);
    pieces.push(
      <VaultReference key={`r${at}`} type={match[1] ?? 'file'} id={match[2] ?? ''} name={match[3] ?? ''} locator={match[4]} />,
    );
    last = at + match[0].length;
  }
  if (last < text.length) pieces.push(<Bold key={`t${last}`} text={text.slice(last)} />);
  return <>{pieces}</>;
}

function Bold({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, index) =>
        part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
          <strong key={index} className="font-semibold text-slate-900">
            {part.slice(2, -2)}
          </strong>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        ),
      )}
    </>
  );
}

// ------------------------------------------------------------ suggestions

function describe(action: CompanionAction): string {
  switch (action.kind) {
    case 'SET_STATUS':
      return `Set your status to ${action.status.toLowerCase()}${action.statusMessage ? ` — “${action.statusMessage}”` : ''}`;
    case 'FOCUS_TASK':
      return `Show “${action.taskTitle}” as what you’re working on`;
    case 'SET_MANUAL_TASK':
      return `Show “${action.manualTask}” as what you’re working on`;
    case 'REQUEST_LEAVE': {
      const range = action.startDate === action.endDate ? action.startDate : `${action.startDate} to ${action.endDate}`;
      const part = action.dayPart && action.dayPart !== 'FULL' ? ` (${action.dayPart.toLowerCase()})` : '';
      return `Request ${action.leaveType.toLowerCase()} leave for ${range}${part}. It goes to your approver as usual.`;
    }
    case 'CREATE_FOLDER':
      return `Create the folder “${action.name}” in ${action.parentName ? `“${action.parentName}”` : 'My files'}`;
    case 'MOVE_ITEM':
      return `Move “${action.itemName}” to ${action.targetFolderName ? `“${action.targetFolderName}”` : 'My files'}`;
    case 'RENAME_ITEM':
      return `Rename “${action.itemName}” to “${action.newName}”`;
    case 'SAVE_DOCUMENT':
      return `Save “${action.name}” to ${action.folderName ? `“${action.folderName}”` : 'My files'} in Files`;
    case 'ATTEND_MEETING':
      return `Go to ${action.roomName} and take notes${action.focus ? ` (listening for: ${action.focus})` : ''}. Everyone there is asked first; only people who agree are noted.`;
    case 'EDIT_DOCUMENT':
      return `Save a new version of “${action.fileName}” with these changes. The current version stays in its history.`;
    case 'SHARE_ITEM':
      return `Give ${action.granteeLabel} ${action.role === 'EDITOR' ? 'edit' : 'view'} access to “${action.itemName}”. They will be able to open it.`;
  }
}

/** The draft Mochi wants to save, so it can be read before it is confirmed. */
function DocumentPreview({ content }: { content: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-2 rounded-2xl bg-white/70 ring-1 ring-inset ring-slate-200">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-xs font-semibold text-slate-700"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
      >
        {expanded ? 'Hide the draft' : 'Read the draft'}
        <ChevronDown className={cx('h-3.5 w-3.5 transition', expanded && 'rotate-180')} aria-hidden />
      </button>
      {expanded ? (
        <div className="max-h-64 overflow-y-auto whitespace-pre-wrap border-t border-slate-200 px-3 py-2 text-xs leading-relaxed text-slate-700">
          <FormattedText text={content} />
        </div>
      ) : null}
    </div>
  );
}

function ProposalCard({ proposal }: { proposal: CompanionProposal }) {
  const { confirm, dismiss } = useCompanion();
  const [pending, setPending] = useState<'confirm' | 'dismiss' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (which: 'confirm' | 'dismiss') => {
    setPending(which);
    setError(null);
    try {
      await (which === 'confirm' ? confirm(proposal) : dismiss(proposal));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That didn’t work.');
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="ml-2 rounded-3xl bg-white/80 p-3 ring-1 ring-inset ring-brand-500/20">
      <p className="text-sm font-semibold text-slate-900">{proposal.summary}</p>
      <p className="mt-0.5 text-xs text-slate-600">{describe(proposal.action)}</p>
      {proposal.action.kind === 'SAVE_DOCUMENT' || proposal.action.kind === 'EDIT_DOCUMENT' ? (
        <DocumentPreview content={proposal.action.content} />
      ) : null}
      {proposal.outcome === 'PENDING' ? (
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            className="btn-secondary px-3 py-1.5 text-xs"
            disabled={pending !== null}
            onClick={() => void run('confirm')}
          >
            {pending === 'confirm' ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Check className="h-3.5 w-3.5" aria-hidden />}
            Confirm
          </button>
          <button
            type="button"
            className="btn-ghost px-3 py-1.5 text-xs"
            disabled={pending !== null}
            onClick={() => void run('dismiss')}
          >
            Not now
          </button>
        </div>
      ) : (
        <p
          className={cx(
            'badge mt-2',
            proposal.outcome === 'CONFIRMED'
              ? 'bg-emerald-400/20 text-emerald-800 ring-1 ring-inset ring-emerald-500/25'
              : 'bg-slate-400/15 text-slate-600 ring-1 ring-inset ring-slate-400/25',
          )}
        >
          {proposal.outcome === 'CONFIRMED' ? 'Done' : 'Dismissed'}
        </p>
      )}
      {error ? (
        <p className="mt-2 text-xs text-rose-700" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
